const REDIRECT_QUERY_PARAMETER_NAMES = new Set([
  "callbackurl",
  "callback_url",
  "next",
  "redirecturl",
  "redirect_url",
  "returnto",
  "return_to",
  "returnurl",
  "return_url",
]);

const RELATIVE_URL_PARSE_BASE = "https://relative-url.invalid";

const getFirstReturnUrlValue = (
  returnUrl: string | string[] | undefined,
): string | undefined => {
  return Array.isArray(returnUrl) ? returnUrl[0] : returnUrl;
};

const decodeReturnUrl = (returnUrl: string): string | null => {
  let decodedReturnUrl = returnUrl;

  for (let i = 0; i < 3; i += 1) {
    try {
      const nextDecodedReturnUrl = decodeURIComponent(decodedReturnUrl);
      if (nextDecodedReturnUrl === decodedReturnUrl) {
        return decodedReturnUrl;
      }

      decodedReturnUrl = nextDecodedReturnUrl;
    } catch {
      return null;
    }
  }

  return decodedReturnUrl;
};

const hasRedirectParameter = (searchParams: URLSearchParams): boolean => {
  let hasRedirectParameterName = false;

  searchParams.forEach((_value, queryParameterName) => {
    if (REDIRECT_QUERY_PARAMETER_NAMES.has(queryParameterName.toLowerCase())) {
      hasRedirectParameterName = true;
    }
  });

  return hasRedirectParameterName;
};

const getHashSearchParams = (hash: string): URLSearchParams => {
  const hashWithoutPrefix = hash.replace(/^#/, "");
  const hashQueryIndex = hashWithoutPrefix.indexOf("?");

  if (hashQueryIndex >= 0) {
    return new URLSearchParams(hashWithoutPrefix.slice(hashQueryIndex + 1));
  }

  return hashWithoutPrefix.includes("=")
    ? new URLSearchParams(hashWithoutPrefix)
    : new URLSearchParams();
};

const hasNestedRedirectParameter = (url: URL): boolean => {
  return (
    hasRedirectParameter(url.searchParams) ||
    hasRedirectParameter(getHashSearchParams(url.hash))
  );
};

export const isSafeRelativeReturnUrl = (returnUrl: string): boolean => {
  if (returnUrl?.trim() !== returnUrl) {
    return false;
  }

  const decodedReturnUrl = decodeReturnUrl(returnUrl);
  if (decodedReturnUrl?.trim() !== decodedReturnUrl) {
    return false;
  }

  if (/[\u0000-\u001F\u007F]/.test(decodedReturnUrl)) {
    return false;
  }

  if (
    !decodedReturnUrl.startsWith("/") ||
    decodedReturnUrl.startsWith("//") ||
    decodedReturnUrl.includes("\\")
  ) {
    return false;
  }

  const parsedReturnUrl = new URL(decodedReturnUrl, RELATIVE_URL_PARSE_BASE);
  if (parsedReturnUrl.pathname.includes("@")) {
    return false;
  }

  if (parsedReturnUrl.origin !== RELATIVE_URL_PARSE_BASE) {
    return false;
  }

  return !hasNestedRedirectParameter(parsedReturnUrl);
};

export const getSafeReturnUrl = (
  returnUrl: string | string[] | undefined,
  fallbackUrl: string,
): string | null => {
  const candidateReturnUrl = getFirstReturnUrlValue(returnUrl);

  if (candidateReturnUrl === undefined) {
    return null;
  }

  const decodedReturnUrl = decodeReturnUrl(candidateReturnUrl);

  return decodedReturnUrl && isSafeRelativeReturnUrl(candidateReturnUrl)
    ? decodedReturnUrl
    : fallbackUrl;
};
